import { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronRight, ListChecks, X } from "lucide-react";
import { create } from "zustand";
import { importFile } from "@/data/import";
import { toast } from "@/ui/toast";
import { IconButton } from "@/ui/IconButton";
import s from "./ImportDialog.module.css";

interface ImportDialogStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useImportDialog = create<ImportDialogStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

export function ImportDialog() {
  const { open, setOpen } = useImportDialog();
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const run = async (files: File[]) => {
    if (files.length === 0 || busy) return;
    setBusy(true);
    try {
      let notes = 0;
      let folders = 0;
      for (const file of files) {
        const res = await importFile(file);
        notes += res.notes;
        folders += res.folders;
      }
      toast.success(`Imported ${notes} note${notes === 1 ? "" : "s"}${folders ? ` in ${folders} folder${folders === 1 ? "" : "s"}` : ""}`);
      setOpen(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        {/* the Vikunja row is the first focusable child, and Radix would
            autofocus it, ringing it and pulling the eye off the drop zone */}
        <Dialog.Content className={s.dialog} onOpenAutoFocus={(e) => e.preventDefault()}>
          <Dialog.Title className={s.title}>Import</Dialog.Title>
          <Dialog.Description className={s.subtitle}>
            Bring notes and tasks from anywhere. Everything becomes plain markdown you own.
          </Dialog.Description>

          {busy ? (
            <div className={s.busy}>Importing… this can take a moment for large archives.</div>
          ) : (
            <div
              className={[s.drop, dragOver ? s.dropActive : ""].join(" ")}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void run([...(e.dataTransfer.files ?? [])]);
              }}
            >
              Drop files here, or click to choose
              <input
                ref={inputRef}
                type="file"
                hidden
                multiple
                accept=".md,.markdown,.txt,.zip,.enex"
                onChange={(e) => void run([...(e.target.files ?? [])])}
              />
            </div>
          )}

          <div className={s.formats}>
            <span className={s.format}>Markdown files / folder zip</span>
            <span className={s.format}>Obsidian vault (zip)</span>
            <span className={s.format}>Notion export</span>
            <span className={s.format}>Trilium export</span>
            <span className={s.format}>Evernote .enex</span>
          </div>

          {/* tasks come from a live api rather than a file, so they get their
              own row here instead of a second entry point elsewhere */}
          <div className={s.divider} />
          <button
            type="button"
            className={s.serviceRow}
            onClick={() => {
              setOpen(false);
              void import("@/vikunja/VikunjaImportDialog").then(({ useVikunjaDialog }) =>
                useVikunjaDialog.getState().openImport(),
              );
            }}
          >
            <ListChecks size={15} />
            <span className={s.serviceBody}>
              <span className={s.serviceName}>Tasks from Vikunja</span>
              <span className={s.serviceHint}>Connect with your instance address and an API token</span>
            </span>
            <ChevronRight size={14} />
          </button>

          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
